/**
 * Google Analytics 4 (gtag.js) Integration & Telemetry Module
 * Measurement ID: G-209LC7GFCZ
 */

export const GA_MEASUREMENT_ID = 'G-209LC7GFCZ';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Generic GA4 Event Dispatcher
 * Safely handles missing window/gtag in SSR or test environments.
 */
export function trackEvent(eventName: string, params: Record<string, any> = {}): void {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch (err) {
    // Fail silently in case of ad-blockers or restricted environments
    console.debug(`[Analytics] Event ${eventName} failed to dispatch:`, err);
  }
}

/**
 * Track DLP prompt preset selection
 */
export function trackDlpPresetSelect(presetKey: 'card' | 'apiSecret' | 'pii' | string, locale: string): void {
  trackEvent('dlp_preset_select', {
    preset_key: presetKey,
    locale,
  });
}

/**
 * Track DLP token vault inspect drawer toggle
 */
export function trackDlpVaultInspect(isOpen: boolean): void {
  trackEvent('dlp_vault_inspect_toggle', {
    state: isOpen ? 'opened' : 'closed',
  });
}

/**
 * Track FAQ item accordion expansion / collapse
 */
export function trackFaqToggle(
  index: number,
  question: string,
  state: 'open' | 'close'
): void {
  trackEvent('faq_accordion_toggle', {
    faq_index: index,
    question_snippet: question.slice(0, 80),
    action: state,
  });
}

/**
 * Track budget quota simulation actions
 */
export function trackBudgetSimulate(
  action: 'simulate_normal' | 'trigger_overdraft' | 'reset_simulation',
  details?: Record<string, any>
): void {
  trackEvent('budget_simulator_action', {
    action_type: action,
    ...details,
  });
}

/**
 * Track currency switcher in budget simulator
 */
export function trackBudgetCurrencyChange(currency: string): void {
  trackEvent('budget_currency_change', {
    currency,
  });
}

/**
 * Track audit log filter change in Analytics showcase
 */
export function trackAnalyticsFilterChange(filter: 'ALL' | 'DLP_ONLY'): void {
  trackEvent('analytics_filter_change', {
    filter_mode: filter,
  });
}

/**
 * Track waitlist signup funnel events
 */
export function trackWaitlistSubmit(
  status: 'attempt' | 'success' | 'duplicate' | 'error',
  metadata: {
    source: string;
    locale: string;
    error?: string;
  }
): void {
  trackEvent('waitlist_submission', {
    status,
    source: metadata.source,
    locale: metadata.locale,
    error_message: metadata.error || null,
  });
}

/**
 * Track theme change (light / dark)
 */
export function trackThemeToggle(theme: 'light' | 'dark'): void {
  trackEvent('theme_toggle', {
    theme,
  });
}

/**
 * Track language change
 */
export function trackLanguageChange(locale: string): void {
  trackEvent('language_change', {
    locale,
  });
}

/**
 * Track in-page navigation clicks (header/footer anchors and CTA buttons)
 */
export function trackNavClick(destination: string, label: string): void {
  trackEvent('navigation_click', {
    destination,
    label,
  });
}

/**
 * Track outbound clicks (e.g. GitHub link)
 */
export function trackOutboundClick(platform: string, url: string): void {
  trackEvent('outbound_click', {
    platform,
    url,
  });
}
